// frontend/src/components/FloodIntelligenceCard.jsx
// Project:      Vision ANPR by Castle Traffic Ltd - Automatic Number Plate Recognition
// Author:       Leon du Plooy - Castle Traffic Ltd - Copyright 2026
// Contact:      dev@castle-traffic.co.uk
// Created:      2026-03-06
// Updated:      2026-09-16
//
// Description:  Reusable flood intelligence card for location-aware UI surfaces.
//               Fetches tenant-safe flood intelligence from the backend using lat/lon
//               and renders a compact operational-impact summary rather than raw telemetry.
//               Intended for Job edit/create flows and dashboard/widget reuse.
//
// Notes:
//               - Job-type aware scoring:
//                   • ATC / tube / counter: standing water + access risk matter most
//                   • Drone / UAV: flood is usually secondary unless rainfall/access issues exist
//                   • ANPR / MCC / static install: access / mount-point relevance matter most
//               - Small-print only. Mobile friendly. No bulky section boxes.
//
// Mobile responsiveness (2026-09-16):
//               - Header/content controls stack safely on narrow screens.
//               - Status chips and refresh control wrap without forcing overflow.
//               - Long location, reason, alert, and technical text can wrap naturally.
//               - Mobile padding is tightened while desktop spacing is preserved.
//               - No scoring, request, persistence, debounce, or flood logic changed.

import React, { useEffect, useMemo, useRef, useState } from "react";
import axiosInstance from "../api/axiosInstance";

const isFiniteNumber = (v) => {
  const n = Number(v);
  return Number.isFinite(n);
};

const formatDateTimeGB = (value) => {
  if (!value) return "—";
  try {
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return "—";
    return d.toLocaleString("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      timeZone: "Europe/London",
    });
  } catch {
    return "—";
  }
};

const formatSigned = (value, digits = 2) => {
  if (value === null || value === undefined || value === "" || !Number.isFinite(Number(value))) {
    return "—";
  }
  const n = Number(value);
  return `${n > 0 ? "+" : ""}${n.toFixed(digits)}`;
};

const formatRain = (value) => {
  if (value === null || value === undefined || value === "" || !Number.isFinite(Number(value))) {
    return "—";
  }
  return `${Number(value).toFixed(1)} mm`;
};

const safeText = (v, fallback = "—") => {
  if (v === null || v === undefined) return fallback;
  const s = String(v).trim();
  return s || fallback;
};

const severityRank = (severityLevel, severityText) => {
  if (Number.isFinite(Number(severityLevel))) return Number(severityLevel);
  const txt = String(severityText || "").toLowerCase();
  if (txt.includes("severe flood warning")) return 1;
  if (txt.includes("flood warning")) return 2;
  if (txt.includes("flood alert")) return 3;
  if (txt.includes("warning no longer")) return 4;
  return 999;
};

const pickBestAlert = (alerts) => {
  if (!Array.isArray(alerts) || alerts.length === 0) return null;
  return alerts
    .slice()
    .sort((a, b) => {
      const ar = severityRank(a?.severity_level, a?.severity);
      const br = severityRank(b?.severity_level, b?.severity);
      if (ar !== br) return ar - br;
      return new Date(b?.time_message_changed || 0) - new Date(a?.time_message_changed || 0);
    })[0];
};

const chipStyle = (tone) => ({
  display: "inline-flex",
  alignItems: "center",
  gap: 8,
  maxWidth: "100%",
  padding: "4px 10px",
  borderRadius: 9999,
  fontSize: 12,
  fontWeight: 700,
  lineHeight: 1.35,
  whiteSpace: "normal",
  overflowWrap: "anywhere",
  background: tone.bg,
  border: `1px solid ${tone.border}`,
  color: tone.text,
});

const impactToneMap = {
  none: {
    bg: "rgba(16, 185, 129, 0.10)",
    border: "rgba(16, 185, 129, 0.28)",
    text: "#047857",
    label: "No Material Impact",
  },
  monitor: {
    bg: "rgba(59, 130, 246, 0.10)",
    border: "rgba(59, 130, 246, 0.24)",
    text: "#1d4ed8",
    label: "Monitor Conditions",
  },
  review: {
    bg: "rgba(245, 158, 11, 0.12)",
    border: "rgba(245, 158, 11, 0.28)",
    text: "#b45309",
    label: "Review Before Deploy",
  },
  likely: {
    bg: "rgba(249, 115, 22, 0.14)",
    border: "rgba(249, 115, 22, 0.32)",
    text: "#c2410c",
    label: "Likely Operational Impact",
  },
};

const classifyJobType = (jobTypeLabel) => {
  const t = String(jobTypeLabel || "").toLowerCase();

  if (
    t.includes("atc") ||
    t.includes("tube") ||
    t.includes("counter") ||
    t.includes("pneumatic") ||
    t.includes("traffic counter")
  ) {
    return "atc";
  }

  if (
    t.includes("drone") ||
    t.includes("uav") ||
    t.includes("aerial")
  ) {
    return "drone";
  }

  if (
    t.includes("anpr") ||
    t.includes("mcc") ||
    t.includes("static") ||
    t.includes("camera") ||
    t.includes("mast") ||
    t.includes("pole")
  ) {
    return "static_install";
  }

  return "generic";
};

const getOperationalImpact = ({ data, jobTypeLabel }) => {
  const bestAlert = pickBestAlert(data?.official_alerts);
  const station = Array.isArray(data?.stations) && data.stations.length ? data.stations[0] : null;
  const rainfall = data?.rainfall || null;
  const tide = data?.tide?.used ? data.tide : null;
  const jobClass = classifyJobType(jobTypeLabel);

  let score = 0;
  const reasons = [];

  const addReason = (points, text) => {
    score += points;
    reasons.push(text);
  };

  const alertSeverity = Number(bestAlert?.severity_level);
  const stationDistance = Number(station?.distance_km);
  const stationDelta6h = Number(station?.delta_6h);
  const rainfall6h = Number(rainfall?.total_last_6h_mm);
  const rainfall24h = Number(rainfall?.total_last_24h_mm);
  const tideDistance = Number(tide?.distance_km);
  const tideDelta6h = Number(tide?.delta_6h);

  // ---- Official alerts: these matter for all road jobs ----
  if (bestAlert) {
    if (alertSeverity === 1 || alertSeverity === 2) {
      addReason(4, `${safeText(bestAlert?.severity)} active in the local area`);
    } else if (alertSeverity === 3) {
      addReason(2, `${safeText(bestAlert?.severity)} active nearby`);
    } else {
      addReason(1, `${safeText(bestAlert?.severity)} present`);
    }
  }

  // ---- Rainfall: strongest for ATC / road access; moderate for drone/static ----
  if (Number.isFinite(rainfall6h)) {
    if (rainfall6h >= 15) {
      addReason(jobClass === "atc" ? 4 : 3, `Heavy rainfall in last 6h (${formatRain(rainfall6h)})`);
    } else if (rainfall6h >= 8) {
      addReason(jobClass === "atc" ? 3 : 2, `Elevated rainfall in last 6h (${formatRain(rainfall6h)})`);
    } else if (rainfall6h >= 3) {
      addReason(jobClass === "atc" ? 2 : 1, `Recent rainfall may leave standing water (${formatRain(rainfall6h)})`);
    }
  }

  if (Number.isFinite(rainfall24h) && rainfall24h >= 20) {
    addReason(jobClass === "atc" ? 2 : 1, `High rainfall accumulation over 24h (${formatRain(rainfall24h)})`);
  }

  // ---- Nearest station / river level ----
  if (station && Number.isFinite(stationDistance)) {
    const rising = String(station?.trend || "").toLowerCase() === "rising";

    if (jobClass === "atc") {
      if (stationDistance <= 0.5 && rising) {
        addReason(3, `Water level rising very close to site (${stationDistance.toFixed(2)} km)`);
      } else if (stationDistance <= 1 && rising) {
        addReason(2, `Water level rising near site (${stationDistance.toFixed(2)} km)`);
      } else if (stationDistance <= 0.5) {
        addReason(1, `Nearby watercourse may affect carriageway edges/access (${stationDistance.toFixed(2)} km)`);
      }
    } else if (jobClass === "static_install") {
      if (stationDistance <= 0.5 && rising) {
        addReason(2, `Rising water close to access/mounting area (${stationDistance.toFixed(2)} km)`);
      } else if (stationDistance <= 0.25) {
        addReason(1, `Very nearby watercourse may affect site access (${stationDistance.toFixed(2)} km)`);
      }
    } else if (jobClass === "drone") {
      // river level usually low relevance to drone unless very close + strong signal
      if (stationDistance <= 0.25 && rising && Number.isFinite(stationDelta6h) && stationDelta6h >= 0.2) {
        addReason(1, `Very close rising water may affect launch/access area`);
      }
    } else {
      if (stationDistance <= 0.5 && rising) {
        addReason(2, `Water level rising close to site`);
      } else if (stationDistance <= 1 && rising) {
        addReason(1, `Nearby rising water level`);
      }
    }
  }

  // ---- Tide: mostly only matters if very near and not for inland noise ----
  if (tide && Number.isFinite(tideDistance)) {
    const tideRelevant = tideDistance <= 1;
    const sharpTideChange = Number.isFinite(tideDelta6h) && Math.abs(tideDelta6h) >= 1.5;

    if (jobClass === "atc") {
      if (tideRelevant && sharpTideChange) {
        addReason(2, `Tidal conditions may affect access or low-lying roadside areas`);
      }
    } else if (jobClass === "static_install") {
      if (tideRelevant && sharpTideChange) {
        addReason(1, `Tidal conditions may affect site access`);
      }
    } else if (jobClass === "drone") {
      // mostly ignore unless clearly access-related
      if (tideDistance <= 0.25 && sharpTideChange) {
        addReason(1, `Tidal conditions may affect launch/access point`);
      }
    } else {
      if (tideRelevant && sharpTideChange) {
        addReason(1, `Tidal conditions relevant near site`);
      }
    }
  }

  let level = "none";
  if (score >= 7) level = "likely";
  else if (score >= 4) level = "review";
  else if (score >= 2) level = "monitor";

  const tone = impactToneMap[level];

  let headline = "";
  if (level === "none") {
    if (jobClass === "atc") {
      headline = "Current flood signals do not suggest material disruption to carriageway counter deployment.";
    } else if (jobClass === "drone") {
      headline = "Current flood signals do not suggest material impact to this drone survey.";
    } else if (jobClass === "static_install") {
      headline = "Current flood signals do not suggest material impact to access or mounting at this site.";
    } else {
      headline = "Current flood signals do not suggest material operational impact at this site.";
    }
  } else if (level === "monitor") {
    headline = "Conditions should be monitored, but current flood intelligence does not yet indicate clear deployment failure.";
  } else if (level === "review") {
    headline = "Flood-related conditions may affect access, setup, or safe deployment. Review before proceeding.";
  } else {
    headline = "Flood-related conditions are likely to affect this job operationally. Do not deploy without review.";
  }

  return {
    jobClass,
    score,
    level,
    tone,
    headline,
    reasons: reasons.slice(0, 3),
    bestAlert,
    station,
    rainfall,
    tide,
  };
};

const FloodIntelligenceCard = ({
  title = "Flood Intelligence",
  lat,
  lon,
  locationLabel = "",
  jobTypeLabel = "",
  persist = true,
  className = "",
  showRefresh = true,
  autoFetch = true,
  showTechnicalFooter = false,
}) => {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");
  const debounceRef = useRef(null);
  const requestKeyRef = useRef("");

  const hasCoords = isFiniteNumber(lat) && isFiniteNumber(lon);
  const numericLat = hasCoords ? Number(lat) : null;
  const numericLon = hasCoords ? Number(lon) : null;

  const loadFlood = async () => {
    if (!hasCoords) {
      setData(null);
      setErr("");
      setLoading(false);
      return;
    }

    const requestKey = `${numericLat}|${numericLon}|${persist ? "1" : "0"}`;
    requestKeyRef.current = requestKey;

    setLoading(true);
    setErr("");

    try {
      const res = await axiosInstance.get("flood/intelligence", {
        params: {
          lat: numericLat,
          lon: numericLon,
          persist: persist ? "true" : "false",
        },
      });

      if (requestKeyRef.current !== requestKey) return;
      setData(res?.data || null);
    } catch (e) {
      if (requestKeyRef.current !== requestKey) return;
      const msg =
        e?.response?.data?.detail ||
        e?.response?.data?.message ||
        e?.message ||
        "Failed to load flood intelligence.";
      setErr(typeof msg === "string" ? msg : JSON.stringify(msg));
      setData(null);
    } finally {
      if (requestKeyRef.current === requestKey) {
        setLoading(false);
      }
    }
  };

  useEffect(() => {
    if (!autoFetch) return undefined;

    if (debounceRef.current) clearTimeout(debounceRef.current);

    if (!hasCoords) {
      setData(null);
      setErr("");
      setLoading(false);
      return undefined;
    }

    debounceRef.current = setTimeout(() => {
      loadFlood();
    }, 500);

    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoFetch, numericLat, numericLon, persist]);

  const impact = useMemo(() => {
    if (!data?.ok) return null;
    return getOperationalImpact({ data, jobTypeLabel });
  }, [data, jobTypeLabel]);

  return (
    <div className={`mt-4 min-w-0 rounded-xl border border-gray-200 bg-white ${className}`}>
      <div className="flex min-w-0 flex-col gap-2 px-3 py-3 sm:flex-row sm:items-start sm:justify-between sm:gap-3 sm:px-4">
        <div className="min-w-0 flex-1">
          <div
            className="text-sm font-semibold break-words"
            style={{ color: "var(--accent-color)" }}
          >
            {title}
          </div>
          <div className="text-xs leading-5 text-gray-600 break-words">
            {hasCoords
              ? `Operational flood impact for pinned location${locationLabel ? ` · ${locationLabel}` : ""}${
                  jobTypeLabel ? ` · ${jobTypeLabel}` : ""
                }`
              : "Set a valid pinned location to enable flood intelligence."}
          </div>
        </div>

        <div className="flex min-w-0 w-full flex-wrap items-center gap-2 sm:w-auto sm:shrink-0 sm:justify-end">
          {hasCoords && impact ? (
            <span className="min-w-0" style={chipStyle(impact.tone)}>
              {impact.tone.label}
            </span>
          ) : (
            <span className="inline-flex max-w-full items-center rounded-full bg-gray-100 px-3 py-1 text-xs font-semibold leading-5 text-gray-600 whitespace-normal break-words">
              No intelligence
            </span>
          )}

          {showRefresh && hasCoords && (
            <button
              type="button"
              onClick={loadFlood}
              disabled={loading}
              className="shrink-0 rounded bg-btnGeneralNormal px-3 py-1 text-xs font-semibold text-white shadow hover:bg-btnGeneralHover active:bg-btnGeneralClick disabled:opacity-60"
              title="Refresh flood intelligence"
            >
              {loading ? "Loading…" : "Refresh"}
            </button>
          )}
        </div>
      </div>

      <div className="px-3 pb-3 sm:px-4 sm:pb-4">
        {!hasCoords ? (
          <div className="text-xs text-gray-600 italic">No valid coordinates available yet.</div>
        ) : loading && !data ? (
          <div className="text-xs text-gray-600 italic">Loading flood intelligence…</div>
        ) : err ? (
          <div className="text-xs text-red-600">❌ {err}</div>
        ) : !data?.ok || !impact ? (
          <div className="text-xs text-gray-600 italic">
            No flood intelligence available for this location.
          </div>
        ) : (
          <div className="min-w-0 rounded-lg border border-gray-100 bg-white p-3 sm:p-4">
            <div
              className="break-words"
              style={{
                fontSize: 13,
                lineHeight: 1.45,
                fontWeight: 600,
                color: "#111827",
              }}
            >
              {impact.headline}
            </div>

            <div className="mt-3 flex min-w-0 flex-wrap items-start gap-2">
              <span className="min-w-0" style={chipStyle(impact.tone)}>
                {impact.tone.label}
              </span>

              {impact.bestAlert?.severity ? (
                <span
                  className="min-w-0"
                  style={chipStyle({
                    bg: "rgba(245, 158, 11, 0.10)",
                    border: "rgba(245, 158, 11, 0.25)",
                    text: "#92400e",
                  })}
                >
                  Alert: {safeText(impact.bestAlert.severity)}
                </span>
              ) : null}

              {Number.isFinite(Number(data?.risk_score)) && (
                <span
                  className="min-w-0"
                  style={chipStyle({
                    bg: "rgba(17, 24, 39, 0.06)",
                    border: "rgba(17, 24, 39, 0.12)",
                    text: "#111827",
                  })}
                >
                  Source score: {Number(data.risk_score)}
                </span>
              )}
            </div>

            <div className="mt-3 min-w-0 space-y-1.5">
              {impact.reasons.length > 0 ? (
                impact.reasons.map((reason, idx) => (
                  <div key={idx} className="text-xs leading-5 text-gray-700 break-words">
                    • {reason}
                  </div>
                ))
              ) : (
                <div className="text-xs leading-5 text-gray-700 break-words">
                  • No operationally relevant flood signal identified for this job type at the pinned location.
                </div>
              )}
            </div>

            {showTechnicalFooter && (
              <div className="mt-3 min-w-0 text-[11px] leading-5 text-gray-500 break-words">
                Generated {formatDateTimeGB(data?.generated_at)}
                {impact.station
                  ? ` · Nearest water level ${safeText(impact.station?.label)} (${Number.isFinite(Number(impact.station?.distance_km)) ? `${Number(impact.station.distance_km).toFixed(2)} km` : "distance unknown"}) · Δ6h ${formatSigned(impact.station?.delta_6h)} ${impact.station?.unit || ""}`
                  : ""}
                {impact.rainfall
                  ? ` · Rain 6h ${formatRain(impact.rainfall?.total_last_6h_mm)} / 24h ${formatRain(impact.rainfall?.total_last_24h_mm)}`
                  : ""}
                {impact.tide
                  ? ` · Tide Δ6h ${formatSigned(impact.tide?.delta_6h)} ${impact.tide?.unit || ""}`
                  : ""}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default FloodIntelligenceCard;